import PageLayout from 'components/pages/PageLayout'
import React from 'react'
import { NextPage, NextPageContext } from 'next'
import { withApollo } from 'components/withApollo'
import PageMeta from 'components/PageMeta'
import FacebookIcon from 'components/icons/FacebookIcon'
import LoginForm from 'components/LoginForm'
import { AtSymbolIcon } from '@heroicons/react/outline'
import EmailLoginForm from 'components/EmailLoginForm'
import Router, { useRouter } from 'next/router'
import { ApolloClient } from '@apollo/client'
import getCurrentUser from 'queries/getCurrentUser'
import Notification from 'components/Notification'

import {
  CustomerLogin,
  CustomerLoginDocument,
  CustomerLoginVariables
} from './types/CustomerLogin'
import { useLoginPageLotInfo } from './types/LoginPageLotInfo'

type PageQuery = {
  redirect_to?: string
  api?: string
  redirect_if_not_authenticated?: string
  auth_token?: string
  auth_email?: string
  checkout?: string
  subscribe_to_lot_id?: string
  notice?: string
}

const LoginPage: NextPage = () => {
  const router = useRouter()
  const params = router.query as PageQuery
  const subscribeToLotId = params.subscribe_to_lot_id
    ? parseInt(params.subscribe_to_lot_id, 10)
    : null
  const [usePhone, setUsePhone] = React.useState(!subscribeToLotId)

  const { data, refetch } = useLoginPageLotInfo({
    variables: {
      lotId: subscribeToLotId!
    },
    skip: !subscribeToLotId,
    fetchPolicy: 'cache-first'
  })

  const lot = data?.lot
  const lotName = lot?.name

  const onLogin = async (
    _customer: { id: number },
    redirectToAfterSignIn?: string | null
  ) => {
    let redirectUrl = '/'
    if (params.redirect_to) {
      redirectUrl = params.redirect_to
    } else if (redirectToAfterSignIn) {
      redirectUrl = redirectToAfterSignIn
    } else if (params.checkout) {
      redirectUrl = '/cart?checkout=true'
    }
    // don't know why, but if this is not loaded explicitly
    // then on redirect there's a "subscribe" button showing
    // even if that page isn't cached
    if (subscribeToLotId) {
      await refetch()
    }
    await router.push(redirectUrl)
  }

  let facebookRedirectUrl = null
  if (params.redirect_to) {
    facebookRedirectUrl = params.redirect_to
  } else if (params.checkout) {
    facebookRedirectUrl = '/cart?checkout=true'
  }

  return (
    <PageLayout fullWidth gray hideFooter>
      <PageMeta
        canonicalPath="/customers/login"
        description="Login or sign up into Best Food Trucks"
        title="Best Food Trucks | Login"
      />
      <div className="flex min-h-full flex-1 flex-col justify-center py-6 md:py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-[480px] px-4">
          {params.notice && (
            <Notification variant="info">{params.notice}</Notification>
          )}
          <div className="mt-10 bg-white px-6 py-6 shadow sm:rounded-lg sm:px-12">
            <h2 className="text-center text-2xl font-semibold tracking-tight text-gray-900 mb-6">
              {subscribeToLotId ? (
                <>
                  Sign up &amp; Subscribe:
                  <br />
                  {lotName}
                </>
              ) : (
                <>Continue with {usePhone ? 'phone' : 'email'}</>
              )}
            </h2>
            {subscribeToLotId && (
              <>
                <p className="mb-8">
                  We need your{' '}
                  <strong>email to send you receipts and truck menus</strong>,
                  as well as your mobile phone number so we can{' '}
                  <strong>text you when your order is ready</strong>.
                </p>
                {lot?.creditForSubscriptions?.cents &&
                lot.creditForSubscriptions.cents > 0 ? (
                  <p className="mb-8">
                    By subscribing you are eligible for{' '}
                    <em>
                      <strong>{lot.creditForSubscriptions.formatted} </strong>
                    </em>
                    off your first online order.
                  </p>
                ) : null}
              </>
            )}
            {usePhone ? (
              <LoginForm onLogin={onLogin} formOnly />
            ) : (
              <EmailLoginForm
                onLogin={onLogin}
                subscribeToLotId={subscribeToLotId}
                formOnly
              />
            )}

            {!subscribeToLotId && (
              <div>
                <div className="relative mt-10">
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 flex items-center"
                  >
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-sm font-medium leading-6">
                    <span className="bg-white px-6 text-gray-900">
                      Or continue with
                    </span>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-2 gap-4">
                  <a
                    className="flex w-full items-center justify-center gap-3 rounded-md bg-[#24292F] px-3 py-1.5 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#24292F]"
                    onClick={() => setUsePhone(!usePhone)}
                    onKeyDown={(e) => {
                      if (e.keyCode === 13) {
                        return setUsePhone(!usePhone)
                      }
                      return null
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <AtSymbolIcon className="h-5 w-5" />
                    <span className="text-sm font-semibold leading-6">
                      {usePhone ? 'Email' : 'Phone'}
                    </span>
                  </a>
                  <a
                    className="flex w-full items-center justify-center gap-3 rounded-md bg-blue-600 px-3 py-1.5 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400"
                    href={
                      facebookRedirectUrl
                        ? `/auth/facebook?as=Customer?redirect_to=${encodeURIComponent(
                            facebookRedirectUrl
                          )}`
                        : `/auth/facebook?as=Customer`
                    }
                  >
                    <FacebookIcon className="w-4 text-white" />
                    <span className="text-sm font-semibold leading-6">
                      Facebook
                    </span>
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  )
}

const getInitialProps = async ({
  res,
  query,
  apolloClient: uncastApolloClient
}: NextPageContext & { apolloClient: ApolloClient<object> }) => {
  // eslint-disable-next-line no-undef
  const apolloClient = uncastApolloClient as ApolloClient<object>
  const existingCustomer = await getCurrentUser({ client: apolloClient })
  let redirectUrl: string | null = null
  const params = query as PageQuery
  if (
    params.auth_token &&
    params.auth_token.length > 0 &&
    params.auth_email &&
    params.auth_email.length > 0
  ) {
    const response = await apolloClient.mutate<
      CustomerLogin,
      CustomerLoginVariables
    >({
      mutation: CustomerLoginDocument,
      variables: {
        email: params.auth_email,
        password: params.auth_token,
        resetTokenAfterLogin: !params.api
      }
    })
    const customer = response?.data?.customerLogin?.customer
    const redirectToAfterSignIn = response?.data?.customerLogin?.redirectTo
    if (customer) {
      if (params.redirect_to) {
        redirectUrl = params.redirect_to
      } else if (redirectToAfterSignIn) {
        redirectUrl = redirectToAfterSignIn
      }
    } else if (params.redirect_if_not_authenticated && params.redirect_to) {
      redirectUrl = params.redirect_to
    }
  } else if (existingCustomer) {
    if (params.redirect_to) {
      redirectUrl = params.redirect_to
    } else if (params.checkout) {
      redirectUrl = '/cart?checkout=true'
    } else {
      redirectUrl = '/'
    }
  }
  if (redirectUrl) {
    if (res) {
      // On the server, we'll use an HTTP response to
      // redirect with the status code of our choice.
      // 307 is for temporary redirects.
      res.writeHead(307, { Location: redirectUrl })
      res.end()
    } else {
      // On the client, we'll use the Router-object
      // from the 'next/router' module.
      Router.replace(redirectUrl)
    }
  }

  return {}
}

LoginPage.getInitialProps = getInitialProps

export default withApollo({ ssr: true })(LoginPage as NextPage)
